import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { COUNTRIES } from "./utils";
import { Sparkles, CheckCircle2 } from "lucide-react";
import { useLocale } from "../../i18n/LocaleContext";

const EMPTY = {
  product_description: "",
  brand_or_mfg: "",
  oem_mfg_part_number: "",
  weblink: "",
  supplier_quotation: "",
  quantity_required: 1,
  required_by_date: "",
  delivery_address: "",
  delivery_country: "",
};

export default function RFQForm({ open, onOpenChange, defaults, onSubmit }) {
  const { t } = useLocale();
  const [form, setForm] = useState({ ...EMPTY, ...(defaults || {}) });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  React.useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, ...(defaults || {}) });
      setResult(null);
      setErrors({});
    }
  }, [open, defaults]);

  const update = (k) => (e) => {
    setForm((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const submit = async () => {
    const next = {};
    if (!form.product_description.trim()) next.product_description = "Required";
    if (!form.quantity_required || Number(form.quantity_required) < 1) next.quantity_required = "Min 1";
    if (!form.required_by_date) next.required_by_date = "Required";
    if (!form.delivery_address.trim()) next.delivery_address = "Required";
    if (!form.delivery_country) next.delivery_country = "Required";
    setErrors(next);
    if (Object.keys(next).length) return;
    setSubmitting(true);
    try {
      const res = await onSubmit({ ...form, quantity_required: Number(form.quantity_required) });
      setResult(res);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setResult(null); onOpenChange(o); }}>
      <DialogContent
        data-testid="rfq-dialog"
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        {!result ? (
          <>
            <DialogHeader>
              <div className="h-11 w-11 rounded-md bg-amber-50 border border-amber-200 text-amber-700 flex items-center justify-center mb-3">
                <Sparkles size={20} />
              </div>
              <DialogTitle className="font-display tracking-tight text-2xl">
                {t("rfq.title")}
              </DialogTitle>
              <DialogDescription>
                {t("rfq.subtitle")}
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
              <div className="md:col-span-2">
                <Label className="text-sm font-semibold">{t("rfq.product_desc")} <span className="text-rose-600">*</span></Label>
                <Input value={form.product_description} onChange={update("product_description")}
                  data-testid="rfq-description" className="mt-1.5" placeholder={t("rfq.product_desc")} />
                {errors.product_description && <p className="text-xs text-rose-600 mt-1">{t("rfq.required")}</p>}
              </div>
              <div>
                <Label className="text-sm font-semibold">{t("rfq.brand_or_mfg")}</Label>
                <Input value={form.brand_or_mfg} onChange={update("brand_or_mfg")} data-testid="rfq-brand" className="mt-1.5" placeholder="e.g., SKF" />
              </div>
              <div>
                <Label className="text-sm font-semibold">{t("rfq.oem_part_number")}</Label>
                <Input value={form.oem_mfg_part_number} onChange={update("oem_mfg_part_number")} data-testid="rfq-oempn" className="mt-1.5" placeholder="e.g., 6205-2RS" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Weblink</Label>
                <Input value={form.weblink} onChange={update("weblink")} data-testid="rfq-weblink" className="mt-1.5" placeholder="https://…" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Supplier quotation (if any)</Label>
                <Input value={form.supplier_quotation} onChange={update("supplier_quotation")} data-testid="rfq-quotation" className="mt-1.5" placeholder="Reference / amount" />
              </div>
              <div>
                <Label className="text-sm font-semibold">{t("rfq.quantity")} <span className="text-rose-600">*</span></Label>
                <Input type="number" min={1} value={form.quantity_required} onChange={update("quantity_required")} data-testid="rfq-qty" className="mt-1.5" />
                {errors.quantity_required && <p className="text-xs text-rose-600 mt-1">{errors.quantity_required}</p>}
              </div>
              <div>
                <Label className="text-sm font-semibold">{t("rfq.target_date")} <span className="text-rose-600">*</span></Label>
                <Input type="text" value={form.required_by_date} onChange={update("required_by_date")} data-testid="rfq-date" className="mt-1.5" placeholder="MM/DD/YYYY" />
                {errors.required_by_date && <p className="text-xs text-rose-600 mt-1">{t("rfq.required")}</p>}
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm font-semibold">Delivery Address <span className="text-rose-600">*</span></Label>
                <Input value={form.delivery_address} onChange={update("delivery_address")} data-testid="rfq-address" className="mt-1.5" placeholder="Full address" />
                {errors.delivery_address && <p className="text-xs text-rose-600 mt-1">{t("rfq.required")}</p>}
              </div>
              <div>
                <Label className="text-sm font-semibold">Delivery Country <span className="text-rose-600">*</span></Label>
                <Select value={form.delivery_country} onValueChange={update("delivery_country")}>
                  <SelectTrigger className="mt-1.5" data-testid="rfq-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (<SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
                {errors.delivery_country && <p className="text-xs text-rose-600 mt-1">{t("rfq.required")}</p>}
              </div>
            </div>

            <Button onClick={submit} disabled={submitting} data-testid="rfq-submit"
              className="mt-2 w-full bg-amber-500 hover:bg-amber-600 text-white rounded-md py-5 font-semibold">
              {submitting ? t("rfq.submitting") : t("rfq.submit")}
            </Button>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="h-11 w-11 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center mb-3">
                <CheckCircle2 size={20} />
              </div>
              <DialogTitle className="font-display tracking-tight text-2xl">RFQ submitted</DialogTitle>
              <DialogDescription>
                Reference: <span className="font-mono text-slate-900 font-semibold">{result.rfq_id}</span>. An Infosys Tactical Buyer will get in touch with updated pricing and other information within {result.sla_business_days} business days.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2 rounded-md bg-slate-50 border border-slate-200 p-4">
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">What happens next</div>
              <ul className="space-y-2 text-sm text-slate-700">
                {result.documented_actions?.map((a, i) => (
                  <li key={i} className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />{a}</li>
                ))}
              </ul>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
